import { matchPath } from 'react-router-dom';
import {
  DashboardCard,
  DashboardChart,
  DashboardColumn,
  DashboardConfig,
  DashboardRow,
  DashboardSection,
} from './charts';
import { Option } from './types';
import { images } from '../theme';
import useRouter from '../hooks/useRouter';
import { SCREEN } from '../theme/breakpoints';

export const isMobile = SCREEN.isMobile;
export const isTablet = SCREEN.isTablet;
export const isWeb = SCREEN.isWeb;

export const isDSDRoute = window.location.host === 'dsd.impactboard.tech' || window.location.host === 'rmt.ecd.gov.za';
export const isCapsiRoute = window.location.host === 'smartboard.capsi.co.za';

export { formatSchoolExportData } from './exports/schoolData';
export { formatActivityStatsData } from './exports/activityProgrammeStats';
export { formatInputStatsData } from './exports/inputProgrammeStats';
export { formatIntitutionsExportData, formatIntitutionsExportDataMasterList } from './exports/institutions';
export {
  formatInstitutionsProfileData,
  formatInterventionsProfileData,
  formatStakeholdersProfileData,
} from './exports/profileData';
export { formatUserReportExportData } from './exports/userReport';
export { parseErrorMessage } from './errors';
export { getCSVData, cleanCSVData, sanitizeData } from './csvs';
export { pwaOfflineQueryFetch, syncPwaOfflineData } from './dataSync';
export {
  hasDefaultInstitutionDashboard,
  isValidCircuit,
  isValidCountry,
  isValidDistrict,
  isValidDistrictMunicipality,
  isValidEcdServiceType,
  isValidEducationSector,
  isValidEmis,
  isValidFundingClass,
  isValidGender,
  isValidGrade,
  isValidInstitutionName,
  isValidInstitutionTrackingCode,
  isValidLanguage,
  isValidLocalMunicipality,
  isValidNewOrRenewalValue,
  isValidPeopleId,
  isValidPopulationGroup,
  isValidProvince,
  isValidRegion,
  isValidRegistrationStatus,
  isValidSchoolPhase,
  isValidSchoolSpecializationGroup,
  isValidSchoolType,
  isValidType,
  isValidVerifiedValue,
  isDate,
  isUndefined,
  validateAccountAndPortal,
  validatePortalFromURL,
  isValidReasonForExit,
  isValidSubjectCode,
  isValidDate,
  validateNumbers,
  validateEmail,
  validateContactNumber,
  validateSAIDNumber,
  isValidNearestPointOfInterest,
  isValidFoodProvider,
  isValidYear, // isValidRegistrationOffice,
} from './validator';
export {
  formatArrayValues,
  formatDBValue,
  stripDBNames,
  formatActivitySessionData,
  formatDeclarationData,
  formatDimensionOptionsCaching,
  formatDropDownEnums,
  formatECDRegistrationData,
  formatError,
  formatFormInstitutionToDBInstitution,
  formatFormRegistrationToDBRegistration,
  formatFrameworkResults,
  formatIndicators,
  formatInstitutionData,
  formatMessage,
  formatMonitoringForms,
  formatOrganisationName,
  formatPassRateData,
  formatProgrammeData,
  formatSelectValue,
  formatString,
  formatStringToEnum,
  formatTemplateData,
  formatUserData,
  formatUserValues,
  formatValues,
  formatfileName,
  mergeCounts,
  stripSelectLabels,
  stripSelectValues,
  insertString,
  getArrayFromObject,
  getIDFromOption,
  getIDArrayFromOptionArray,
  formatSubjectValues,
  formatCohortData,
  formatSubmissionData,
  formatFormInstitutionToDBInstitutionForBulkUpload,
  formatPeopleForFlatfileTemplate,
  formatRegistrationsForBulkUpload,
  formatUserOrganisations,
} from './format';
export {
  clearIndexedDB,
  deleteChangedInstitutionsList,
  deleteChangedRegistrationsList,
  getAllInstitutionTypes,
  getAllMinistries,
  getChangedInstitutionsList,
  getChangedRegistrationsList,
  getECDRegistrationById,
  getECDRegistrationsListPerInstitution,
  getInstitutionById,
  getInstitutionsList,
  getECDReqDocsByRegType,
  getNCBSurvey,
  getPCFSurvey,
  getPortalOrganisation,
  getAllCountries,
  getAllProvinces,
  getAllDistrictMunicipalities,
  getAllLocalMunicipalities,
  getECDRegistrationTypes,
  readFromIndexedDB,
  upsertInstitutionLocally,
  upsertRegistrationLocally,
  upsertPersonLocally,
  writeInstitutionTypes,
  writeMinistries,
  writeECDRegistrationsList,
  writeECDRequiredDocumentsList,
  writeInstitutionsList,
  writeNCBSurvey,
  writePCFSurvey,
  writeToIndexedDB,
  writePortalOrganisation,
  writeCountries,
  writeProvinces,
  writeDistrictMunicipalities,
  writeLocalMunicipalities,
  writeDistricts,
  writeECDRegistrationTypes,
  writeUserInformation,
} from './idbFns';
export { subscribeToNotifications } from './subscription';
export { fileToBase64, onComplete, uploadToS3, ApolloGraphQLUploads } from './upload';
export { isTokenValid, isLoggedIn } from './session';
export {
  flatfileInstitutionValidator,
  flatfileMonitoringFormValidator,
  flatfilePeopleValidator,
} from './flatfile-validators';
// export { ProgrammeStorage } from './programmeStorage';
export { default as ECDRegistrationUtil } from './ecdRegistrationStorage';
export { ListController } from './survey-builder';
export { Card, Chart, Column, getChartData, Row, Section } from './charts';
export interface CardInterface extends DashboardCard {}
export interface ChartInterface extends DashboardChart {}
export interface ColumnInterface extends DashboardColumn {}
export interface ConfigInterface extends DashboardConfig {}
export interface RowInterface extends DashboardRow {}
export interface SectionInterface extends DashboardSection {}
export { device } from './device';
export { lightenDarkenColor } from './style';
export { savePDF } from './savePdf';

export const defaultRedirectRouteToPortalCheck = {
  '/admin': 'ADMIN',
  '/funder': 'FUNDER',
  '/institution': 'INSTITUTION',
  '/service-provider': 'SERVICE_PROVIDER',
};

export const isEmpty = (value: any) => {
  return (
    value == null || // From standard.js: Always use === - but obj == null is allowed to check null || undefined
    (typeof value === 'object' && Object.keys(value).length === 0) ||
    (typeof value === 'string' && value.trim().length === 0)
  );
};

export const getBooleanFromString = (value: string): boolean => {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return Boolean(value);
};

export const getBooleanFromOption = (value: Option | undefined): boolean => {
  if (value === undefined) return false;
  if (value.value === 'true') return true;
  if (value.value === 'false') return false;
  return Boolean(value.value);
};

export const getPortal = (path?: string): string => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { match } = useRouter();

  const result = matchPath(path ? path : match.path, {
    path: '/:portal/',
  });

  if (result && result.params['portal']) {
    return result.params['portal'] || '';
  }
  return '';
};

export function getCustomBackgroundImage(): string {
  const customBackgroundImage = isDSDRoute
    ? images.dsdLandingBackground
    : isCapsiRoute
    ? images.capsiLandingBackground
    : images.landingBackground;

  return customBackgroundImage;
}

export function getCustomLogoImage(): string {
  const customLogoImage = isDSDRoute ? images.dsdLogo : isCapsiRoute ? images.capsiLogo : images.impactBoardLogo;

  return customLogoImage;
}

export const deepCopy = <T, U = T extends Array<infer V> ? V : never>(source: T): T => {
  if (Array.isArray(source)) {
    return source.map(item => deepCopy(item)) as T & U[];
  }
  if (source instanceof Date) {
    return new Date(source.getTime()) as T & Date;
  }
  if (source && typeof source === 'object') {
    return (Object.getOwnPropertyNames(source) as (keyof T)[]).reduce<T>((o, prop) => {
      Object.defineProperty(o, prop, Object.getOwnPropertyDescriptor(source, prop)!);
      o[prop] = deepCopy(source[prop]);
      return o;
    }, Object.create(Object.getPrototypeOf(source)));
  }
  return source;
};

export const sleep = (delayInMilliseconds: number) => new Promise(resolve => setTimeout(resolve, delayInMilliseconds));
